import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";

function LangToggle({ compact = false }) {
  const { lang, setLang } = useLanguage();
  const base = "px-3 h-8 rounded-full text-xs font-medium transition-colors duration-200";
  return (
    <div
      data-testid={compact ? "lang-toggle-mobile" : "lang-toggle"}
      className="inline-flex items-center gap-1 rounded-full glass border border-white/10 p-1"
    >
      <button
        type="button"
        data-testid={compact ? "lang-btn-en-mobile" : "lang-btn-en"}
        onClick={() => setLang("en")}
        className={`${base} ${lang === "en" ? "bg-white text-black" : "text-zinc-300 hover:text-white"}`}
      >
        English
      </button>
      <button
        type="button"
        data-testid={compact ? "lang-btn-ar-mobile" : "lang-btn-ar"}
        onClick={() => setLang("ar")}
        className={`${base} ${lang === "ar" ? "bg-white text-black" : "text-zinc-300 hover:text-white"}`}
      >
        العربية
      </button>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,border,padding] duration-300 ${
        scrolled ? "glass border-b border-white/10 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 md:px-10">
        <a href="#top" data-testid="header-logo" className="flex items-center gap-3">
          <img src={BRAND.logo} alt="Paigez.ai" className="h-14 md:h-16 w-auto" />
        </a>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {t.nav.links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="text-sm text-zinc-300 hover:text-white transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LangToggle />
          <Button
            asChild
            data-testid="header-cta-download"
            className="rounded-full bg-white text-black hover:bg-zinc-200 px-5 h-10 font-medium"
          >
            <a href="#download">{t.nav.cta}</a>
          </Button>
        </div>

        <button
          data-testid="header-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/10 bg-white/5"
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden border-t border-white/10 glass"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {t.nav.links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.href.replace("#", "")}`}
                  className="text-base text-zinc-200"
                >
                  {l.label}
                </a>
              ))}
              <div className="mt-1">
                <LangToggle compact />
              </div>
              <a
                href="#download"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta-download"
                className="mt-2 inline-flex justify-center rounded-full bg-white text-black py-3 font-medium"
              >
                {t.nav.cta}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
